/**
 * AuthContext — wrapper sobre Supabase Auth.
 *
 * F5a expone:
 *   - Sesión (`user`, `session`) sincronizada con `onAuthStateChange`.
 *   - Perfil (`profile`) consultado vía React Query (key `['profile', userId]`).
 *   - Bandera `isAdmin` derivada de `profile.role`.
 *   - Acciones: `signIn`, `signUp`, `signOut`, `recoverPassword`.
 *   - Helper `ensureProfile`: upsert idempotente del registro de la tabla
 *     `profiles` cuando el usuario inicia sesión, respetando RLS (id = auth.uid()).
 *
 * Coexistencia useQuery + onAuthStateChange:
 *   - `onAuthStateChange` mantiene `session/user` en useState (fuente de verdad
 *     de la auth).
 *   - `useQuery(['profile', userId])` se habilita SOLO cuando hay user.id;
 *     ante un SIGNED_OUT invalidamos la key para forzar `profile=null`.
 *   - Tras un SIGNED_IN llamamos `ensureProfile` y refetcheamos el query, así
 *     el primer login expone un profile con role por defecto sin colisionar
 *     con cualquier admin promovido manualmente en backend.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../lib/supabase/client';
import type { Tables } from '../lib/supabase/types';
import { setUserContext, clearUserContext } from '../lib/sentry';

export type Profile = Tables<'profiles'>;

export interface AuthSignInResult {
  error?: Error;
}

export interface AuthSignUpResult {
  error?: Error;
  /** True si Supabase requiere confirmación por email (no hay session todavía). */
  needsEmailConfirm: boolean;
}

export interface AuthRecoverResult {
  error?: Error;
}

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<AuthSignInResult>;
  signUp: (email: string, password: string, fullName?: string) => Promise<AuthSignUpResult>;
  recoverPassword: (email: string) => Promise<AuthRecoverResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * `ensureProfile` — upsert idempotente en `profiles` para el usuario actual.
 *
 * RLS exige que `id = auth.uid()`. Usamos `upsert({ onConflict: 'id', ignoreDuplicates: true })`
 * para que un usuario existente no sobreescriba sus campos (full_name, role,
 * phone, etc. quedan tal como están). Solo el primer login crea el registro.
 *
 * `role` se omite del payload: el default de la tabla (`customer`) aplica al
 * insert nuevo y no se toca en filas existentes.
 */
async function ensureProfile(user: User): Promise<void> {
  const fullName =
    (user.user_metadata?.full_name as string | undefined) ??
    (user.user_metadata?.name as string | undefined) ??
    null;

  const { error } = await supabase.from('profiles').upsert(
    {
      id: user.id,
      full_name: fullName,
    },
    { onConflict: 'id', ignoreDuplicates: true },
  );

  if (error) {
    // No tiramos: ensureProfile es best-effort. El siguiente refresh del query
    // expondrá el profile real o, si la inserción falló, el componente
    // consumidor lo verá como `null` y podrá reintentar.
    console.warn('[auth] ensureProfile error:', error.message);
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const queryClient = useQueryClient();
  // Evita ejecuciones duplicadas de ensureProfile en re-fireos del event stream.
  const ensuredForUserId = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const initial = data.session ?? null;
      setSession(initial);
      setLoading(false);
      // Si ya hay sesión persistida al boot, dispara ensureProfile una vez.
      if (initial?.user && ensuredForUserId.current !== initial.user.id) {
        ensuredForUserId.current = initial.user.id;
        // Setea el contexto de Sentry con la sesión persistida (no-op si Sentry
        // está deshabilitado por env).
        setUserContext({ id: initial.user.id, email: initial.user.email ?? null });
        void ensureProfile(initial.user).then(() => {
          queryClient.invalidateQueries({ queryKey: ['profile', initial.user.id] });
        });
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s ?? null);
      if (event === 'SIGNED_IN' && s?.user) {
        setUserContext({ id: s.user.id, email: s.user.email ?? null });
        if (ensuredForUserId.current !== s.user.id) {
          ensuredForUserId.current = s.user.id;
          void ensureProfile(s.user).then(() => {
            queryClient.invalidateQueries({ queryKey: ['profile', s.user.id] });
          });
        }
      }
      if (event === 'SIGNED_OUT') {
        ensuredForUserId.current = null;
        clearUserContext();
        // Limpia el cache del profile para que `profile` sea null inmediatamente.
        queryClient.removeQueries({ queryKey: ['profile'] });
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [queryClient]);

  const user = session?.user ?? null;
  const userId = user?.id ?? null;

  // Profile vía React Query — sólo si hay user. staleTime 60s para reducir
  // re-fetch en navegación interna; el evento SIGNED_IN invalida la key.
  const profileQuery = useQuery<Profile | null>({
    queryKey: ['profile', userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        throw new Error(error.message);
      }
      return data ?? null;
    },
  });

  const profile = profileQuery.data ?? null;
  const isAdmin = profile?.role === 'admin';

  const signIn = useCallback(async (email: string, password: string): Promise<AuthSignInResult> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: new Error(error.message) };
    return {};
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, fullName?: string): Promise<AuthSignUpResult> => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: fullName ? { full_name: fullName } : undefined,
          emailRedirectTo: `${window.location.origin}/auth/login`,
        },
      });
      if (error) {
        return { error: new Error(error.message), needsEmailConfirm: false };
      }
      // Supabase Auth con email confirmation activa devuelve `user` pero NO
      // `session`. Sin email confirmation devuelve ambos y `onAuthStateChange`
      // disparará ensureProfile automáticamente.
      const needsEmailConfirm = Boolean(data.user) && !data.session;
      return { needsEmailConfirm };
      // TODO: implementar magic link (signInWithOtp) cuando el flujo lo requiera.
    },
    [],
  );

  const recoverPassword = useCallback(async (email: string): Promise<AuthRecoverResult> => {
    // QA Auth 2026-06-25: el SDK `supabase-js` LANZA `TypeError: Failed to
    // fetch` cuando la red está caída o el endpoint /auth/v1/recover no
    // responde (proyecto pausado, GoTrue degradado, DNS roto) en lugar de
    // devolver `{ error }`. Sin try/catch esa excepción se propaga al
    // handler del componente y el usuario ve un toast literal con
    // "Failed to fetch" — indescifrable. El catch normaliza ambos
    // caminos: error de aplicación (devuelto) y excepción de transporte
    // (lanzada), y devuelve siempre un `Error` con mensaje legible.
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset`,
      });
      if (error) {
        // Logueamos el error ORIGINAL en consola para depuración (dev de
        // turno necesita el código/payload real). El usuario sólo ve el
        // mensaje normalizado en el toast.
        console.error('[auth] resetPasswordForEmail returned error:', error);
        return { error: new Error(error.message) };
      }
      return {};
    } catch (err) {
      console.error('[auth] resetPasswordForEmail threw:', err);
      if (err instanceof TypeError) {
        // Red caída / DNS / CORS / proyecto Supabase pausado.
        return {
          error: new Error(
            'No pudimos conectarnos al servidor. Revisa tu conexión e inténtalo de nuevo.',
          ),
        };
      }
      return {
        error:
          err instanceof Error
            ? err
            : new Error('Ocurrió un error inesperado al enviar el enlace.'),
      };
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      profile,
      loading,
      isAdmin,
      signIn,
      signUp,
      recoverPassword,
      signOut,
    }),
    [user, session, profile, loading, isAdmin, signIn, signUp, recoverPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
